import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

export default function AdminDiscovery() {
    const [pending, setPending] = useState([]);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState("");
    const [scanning, setScanning] = useState(false);

    const reload = useCallback(() => {
        api.get("/admin/discovery/pending").then((r) => setPending(r.data.items || [])).catch(() => {});
        api.get("/admin/discovery/status").then((r) => setStatus(r.data)).catch(() => {});
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const approve = async (slug) => {
        setBusy(slug);
        try {
            await api.post(`/admin/discovery/${slug}/approve`);
            reload();
        } finally { setBusy(""); }
    };
    const reject = async (slug) => {
        setBusy(slug);
        try {
            await api.post(`/admin/discovery/${slug}/reject`);
            reload();
        } finally { setBusy(""); }
    };
    const runNow = async () => {
        if (!window.confirm("Lancer un scan Google Places maintenant ? (~2 min, consomme du quota API)")) return;
        setScanning(true);
        try {
            await api.post("/admin/discovery/run");
            reload();
        } finally { setScanning(false); }
    };

    const lastScan = status?.last_scan;

    return (
        <section className="border border-cp-border p-6 md:p-10 bg-cp-ivoire" data-testid="admin-discovery">
            <div className="flex items-baseline justify-between gap-6 mb-8">
                <div>
                    <h2 className="cp-serif text-3xl md:text-4xl">Découverte automatique</h2>
                    <p className="cp-tiny text-cp-gris mt-2">
                        Scan hebdomadaire (dimanche 02h UTC) — Google Places — note ≥ 4.0, ≥ 10 avis, code postal vérifié
                    </p>
                </div>
                <button
                    onClick={runNow}
                    disabled={scanning}
                    data-testid="discovery-run-now"
                    className="cp-btn-primary text-sm whitespace-nowrap"
                >
                    {scanning ? "Scan en cours…" : "Scanner maintenant"}
                </button>
            </div>

            {/* Status panel */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                <div className="border border-cp-border p-5">
                    <p className="cp-tiny text-cp-terre mb-2">À valider</p>
                    <p className="cp-serif text-4xl text-cp-foret">{status?.pending_count ?? "…"}</p>
                </div>
                <div className="border border-cp-border p-5">
                    <p className="cp-tiny text-cp-terre mb-2">Dernier scan</p>
                    <p className="cp-serif text-lg">
                        {lastScan?.scanned_at ? new Date(lastScan.scanned_at).toLocaleString("fr-FR") : "Jamais"}
                    </p>
                </div>
                <div className="border border-cp-border p-5">
                    <p className="cp-tiny text-cp-terre mb-2">Trouvées au dernier scan</p>
                    <p className="cp-serif text-lg">
                        {lastScan?.inserted_pending ?? 0} <span className="text-cp-gris">/{lastScan?.total_seen ?? 0} examinées</span>
                    </p>
                </div>
            </div>

            {/* Pending list */}
            {pending.length === 0 ? (
                <p className="cp-serif italic text-cp-gris">Aucune candidate en attente. Cliquez sur «Scanner maintenant» pour rechercher de nouvelles adresses.</p>
            ) : (
                <div className="space-y-4" data-testid="discovery-pending-list">
                    {pending.map((p) => (
                        <div key={p.slug} className="border border-cp-border p-5 flex flex-col md:flex-row md:items-center gap-4">
                            <div className="flex-1 min-w-0">
                                <p className="cp-tiny text-cp-terre mb-1">
                                    {p.city_slug?.replace(/-/g, " ")} · {p.category}
                                </p>
                                <h3 className="cp-serif text-xl mb-1">{p.name}</h3>
                                <p className="text-sm text-cp-gris truncate">{p.address}</p>
                                <p className="text-xs text-cp-gris mt-1">
                                    ★ {p.google_rating?.toFixed?.(1) ?? "?"} · {p.google_user_ratings_total ?? 0} avis
                                    {p.google_maps_url && (
                                        <> · <a href={p.google_maps_url} target="_blank" rel="noopener noreferrer" className="cp-link">voir sur Google Maps ↗</a></>
                                    )}
                                </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <button
                                    onClick={() => approve(p.slug)}
                                    disabled={busy === p.slug}
                                    data-testid={`discovery-approve-${p.slug}`}
                                    className="px-4 py-2 bg-cp-foret text-cp-ivoire text-sm hover:bg-cp-foret/85 disabled:opacity-50"
                                >
                                    Publier
                                </button>
                                <button
                                    onClick={() => reject(p.slug)}
                                    disabled={busy === p.slug}
                                    data-testid={`discovery-reject-${p.slug}`}
                                    className="px-4 py-2 border border-cp-border text-cp-gris text-sm hover:bg-cp-craie disabled:opacity-50"
                                >
                                    Rejeter
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
